import React, { useEffect, useState } from 'react';
import { FaEye, FaEyeSlash, FaSave } from 'react-icons/fa';
import {
    DEFAULT_NAVBAR_CONFIG,
    NAVBAR_LINKS,
    NavbarLinkConfig,
    NavbarLinkKey,
    normalizeNavbarConfig
} from '../../data/navbarConfig';
import styles from './styles/NavbarSettings.module.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const NavbarSettings: React.FC = () => {
    const [links, setLinks] = useState<NavbarLinkConfig[]>(DEFAULT_NAVBAR_CONFIG);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');

    useEffect(() => {
        fetch(`${API_URL}/navbar-config`)
            .then(response => {
                if (!response.ok) throw new Error('No se pudo cargar la configuración');
                return response.json();
            })
            .then(data => setLinks(normalizeNavbarConfig(data.links)))
            .catch(err => setError(err.message))
            .finally(() => setLoading(false));
    }, []);

    const setPosition = (position: number, selectedKey: string) => {
        setMessage('');
        setError('');

        if (!selectedKey) {
            setLinks(current => current.map((link, index) =>
                index === position ? { ...link, visible: false } : link
            ));
            return;
        }

        const key = selectedKey as NavbarLinkKey;
        setLinks(current => {
            const selectedIndex = current.findIndex(link => link.key === key);
            const next = [...current];
            if (selectedIndex >= 0 && selectedIndex !== position) {
                [next[position], next[selectedIndex]] = [next[selectedIndex], next[position]];
            }
            next[position] = { ...next[position], visible: true };
            return next;
        });
    };

    const toggleVisibility = (position: number) => {
        setMessage('');
        setLinks(current => current.map((link, index) =>
            index === position ? { ...link, visible: !link.visible } : link
        ));
    };

    const save = async () => {
        setSaving(true);
        setMessage('');
        setError('');
        const payload = links.map((link, orden) => ({ ...link, orden }));

        try {
            const response = await fetch(`${API_URL}/admin/navbar-config`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ links: payload })
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.error || 'No se pudo guardar');
            setLinks(payload);
            setMessage('Navbar guardado. Los cambios ya están visibles en el sitio.');
            window.dispatchEvent(new CustomEvent('navbar-config-updated', { detail: payload }));
        } catch (err) {
            setError(err instanceof Error ? err.message : 'No se pudo guardar');
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <div className={styles.status}>Cargando configuración...</div>;

    return (
        <section className={styles.panel}>
            <div className={styles.header}>
                <div>
                    <span>Configuración del sitio</span>
                    <h2>Links del navbar</h2>
                    <p>Elegí el link de cada posición y ocultá los que no quieras mostrar.</p>
                </div>
                <button type="button" className={styles.saveButton} onClick={save} disabled={saving}>
                    <FaSave /> {saving ? 'Guardando...' : 'Guardar cambios'}
                </button>
            </div>

            <div className={styles.preview}>
                <strong>Vista previa</strong>
                <div>
                    {links.filter(link => link.visible).map(link => (
                        <span key={link.key}>{NAVBAR_LINKS[link.key].label}</span>
                    ))}
                </div>
            </div>

            <div className={styles.positions}>
                {links.map((link, index) => (
                    <div className={`${styles.row} ${!link.visible ? styles.hiddenRow : ''}`} key={`${index}-${link.key}`}>
                        <span className={styles.position}>Posición {index + 1}</span>
                        <select
                            value={link.visible ? link.key : ''}
                            onChange={event => setPosition(index, event.target.value)}
                            aria-label={`Link en la posición ${index + 1}`}
                        >
                            <option value="">No mostrar esta posición</option>
                            {(Object.keys(NAVBAR_LINKS) as NavbarLinkKey[]).map(key => (
                                <option key={key} value={key}>{NAVBAR_LINKS[key].label}</option>
                            ))}
                        </select>
                        <button
                            type="button"
                            className={styles.visibilityButton}
                            onClick={() => toggleVisibility(index)}
                            aria-label={link.visible ? `Ocultar ${NAVBAR_LINKS[link.key].label}` : `Mostrar ${NAVBAR_LINKS[link.key].label}`}
                        >
                            {link.visible ? <FaEye /> : <FaEyeSlash />}
                            {link.visible ? 'Visible' : 'Oculto'}
                        </button>
                    </div>
                ))}
            </div>

            {message && <div className={styles.success}>{message}</div>}
            {error && <div className={styles.error}>{error}</div>}
        </section>
    );
};

export default NavbarSettings;
